/**
 * vaultKeyV2.ts — the key model for the credential Vault.
 *
 * Read vaultUnlock.ts first. New vaults use a random VEK the server holds wrapped
 * under a key outside the database, so the server can open them and the database
 * can't. Everything below about a wrap under the account root key describes the
 * older vaults that use one, and is what `openVaultKey` still opens.
 *
 * ── Why this exists ────────────────────────────────────────────────────────
 * v1 DERIVES the Vault Encryption Key:
 *
 *   VEK = PBKDF2(`${accountId}:${projectId}:vault-v1`, serverSalt, 100k)
 *
 * Every input is a value the server holds — accountId and projectId are
 * columns, and the salt is minted server-side by randomBytes(32) into
 * projects.blob.vault_salt. The backend can therefore derive every VEK in the
 * product. It doesn't, because no server code calls it, which makes
 * zero-knowledge a deployment convention rather than a property.
 *
 * v2 WRAPS instead of deriving. The VEK is random and never derivable from
 * anything; it is stored only as ciphertext under a KEK computed from the
 * account root key, which the server holds ONLY wrapped under a recovery
 * passphrase it never receives (accountRootKey.ts / recoveryMnemonic.ts).
 * Server state alone is then genuinely insufficient.
 *
 * ── Conventions are borrowed, not invented ─────────────────────────────────
 * The KEK derivation mirrors deriveSnapshotKeyV2 (snapshotCrypto.ts:102-110)
 * exactly — same HKDF-SHA256, same IKM, same `eleplan-<domain>-v2:account:project`
 * info shape. Snapshots already got this right; the vault is the outlier, and
 * matching them means one mental model rather than two.
 *
 * The wrap reuses the house AES-256-GCM conventions: hex everywhere, the GCM
 * tag split off the tail (vaultCrypto.ts, recoveryMnemonic.ts), a `kdf` tag
 * checked on read, and null-on-any-failure so callers cannot accidentally
 * branch on why decryption failed.
 *
 * ── The one addition: AAD ──────────────────────────────────────────────────
 * The KEK already binds account and project through `info`. The AAD binds the
 * PRINCIPAL and the EPOCH, which the KEK does not cover. Without it, a
 * compromised server could serve one row's wrap for a request scoped to
 * another, and the moment a project carries a second principal (a shared
 * plan, a recovery principal) the two become silently interchangeable. It
 * costs nothing and closes that door before it opens.
 */

import * as Crypto from 'expo-crypto';
import { gcm } from '@noble/ciphers/aes.js';
import { hkdf } from '@noble/hashes/hkdf.js';
import { sha256 } from '@noble/hashes/sha2.js';
import { bytesToHex, hexToBytes, utf8ToBytes } from '@noble/ciphers/utils.js';

/** Which key model a stored credential was encrypted under. */
export const VAULT_KEY_SCHEME_V1 = 1 as const;
export const VAULT_KEY_SCHEME_V2 = 2 as const;
export type VaultKeyScheme = typeof VAULT_KEY_SCHEME_V1 | typeof VAULT_KEY_SCHEME_V2;

export const VAULT_WRAP_VERSION = 2 as const;
export const VAULT_WRAP_KDF = 'hkdf-sha256' as const;

/**
 * Who can unwrap a given VEK. v2.0 ships only the account root key; the field
 * exists so adding a second principal later is a new row rather than a
 * migration, and so the AAD already binds it.
 */
export const PRINCIPAL_ACCOUNT_ROOT = 'account_root' as const;

/**
 * Scheme dispatch. Rows written before v2 carry no marker at all, so an
 * absent or unrecognised value MUST read as v1 — treating unknown as v2 would
 * make a legacy credential permanently unreadable.
 */
export function getKeyScheme(row: { key_scheme?: number | null } | null | undefined): VaultKeyScheme {
  return row?.key_scheme === VAULT_KEY_SCHEME_V2 ? VAULT_KEY_SCHEME_V2 : VAULT_KEY_SCHEME_V1;
}

/** A random 32-byte per-project VEK. Never derived from anything. */
export async function generateVaultKey(): Promise<Uint8Array> {
  return new Uint8Array(await Crypto.getRandomBytesAsync(32));
}

/**
 * A public identifier for a VEK: SHA-256 over the key, hex.
 *
 * Stored server-side so the database can enforce that every principal on a
 * project wraps the SAME key. Publishing it is safe — the preimage is 32
 * uniformly random bytes, so there is no dictionary and no feasible search —
 * and it converts "two principals disagree about the key" from a bug that
 * silently orphans credentials into a write that fails.
 */
export function vekFingerprint(vek: Uint8Array): string {
  return bytesToHex(sha256(vek));
}

/**
 * The HKDF salt for the wrap, generated on the CLIENT.
 *
 * Deliberately not reusing projects.blob.vault_salt: that value is minted
 * server-side, and reusing it would leave a server-controlled input in the v2
 * derivation — the exact flaw being fixed. HKDF salts need not be secret, but
 * they must not be attacker-chosen.
 */
export async function generateVaultWrapSalt(): Promise<string> {
  return bytesToHex(new Uint8Array(await Crypto.getRandomBytesAsync(32)));
}

/**
 * KEK for one (account, project, principal).
 *
 * Mirrors deriveSnapshotKeyV2. Synchronous and sub-millisecond — unlike v1's
 * 100k-iteration PBKDF2, which is why the web surface currently refuses to
 * cache a key and pays 1-2s on every vault open. v2 removes that cost.
 */
export function deriveVaultKek(
  accountRootKey: Uint8Array,
  accountId: string,
  projectId: number | string,
  wrapSaltHex: string,
): Uint8Array {
  const info = utf8ToBytes(`eleplan-vault-v2:${accountId}:${projectId}`);
  const salt = hexToBytes(wrapSaltHex);
  return hkdf(sha256, accountRootKey, salt, info, 32);
}

/** The wrapped VEK, as stored. Field-per-column, matching RecoveryWrap. */
export interface VaultKeyWrap {
  wrap_version: typeof VAULT_WRAP_VERSION;
  kdf: typeof VAULT_WRAP_KDF;
  principal_type: string;
  principal_id: string;
  vek_epoch: number;
  wrap_salt_hex: string;   // 64 hex — 32 bytes
  wrap_iv_hex: string;     // 24 hex — 12 bytes
  wrapped_vek_hex: string; // 64 hex — 32 bytes
  wrap_tag_hex: string;    // 32 hex — 16 bytes
}

export interface WrapMeta {
  accountId: string;
  projectId: number | string;
  wrapSaltHex: string;
  principalType?: string;
  principalId: string;
  vekEpoch?: number;
}

/** Binds a wrap to its exact storage slot — see the AAD note in the header. */
function wrapAAD(
  accountId: string,
  projectId: number | string,
  principalType: string,
  principalId: string,
  vekEpoch: number,
): Uint8Array {
  return utf8ToBytes(
    `eleplan-vault-v2|${accountId}|${projectId}|${principalType}:${principalId}|${vekEpoch}`,
  );
}

export async function wrapVaultKey(
  vek: Uint8Array,
  kek: Uint8Array,
  meta: WrapMeta,
): Promise<VaultKeyWrap> {
  if (vek.length !== 32) throw new Error('VEK must be 32 bytes.');
  if (kek.length !== 32) throw new Error('KEK must be 32 bytes.');

  const principalType = meta.principalType ?? PRINCIPAL_ACCOUNT_ROOT;
  const vekEpoch = meta.vekEpoch ?? 1;
  const iv = new Uint8Array(await Crypto.getRandomBytesAsync(12));
  const aad = wrapAAD(meta.accountId, meta.projectId, principalType, meta.principalId, vekEpoch);
  // 48 bytes out: 32 ciphertext || 16 tag.
  const sealed = gcm(kek, iv, aad).encrypt(vek);

  return {
    wrap_version: VAULT_WRAP_VERSION,
    kdf: VAULT_WRAP_KDF,
    principal_type: principalType,
    principal_id: meta.principalId,
    vek_epoch: vekEpoch,
    wrap_salt_hex: meta.wrapSaltHex,
    wrap_iv_hex: bytesToHex(iv),
    wrapped_vek_hex: bytesToHex(sealed.slice(0, -16)),
    wrap_tag_hex: bytesToHex(sealed.slice(-16)),
  };
}

/**
 * Unwrap, or null.
 *
 * Never throws and never distinguishes failure modes — a caller that could
 * tell "wrong key" from "tampered ciphertext" would leak an oracle, and every
 * caller's correct response is identical anyway: route the user through
 * recovery.
 */
export function unwrapVaultKey(
  wrap: VaultKeyWrap | null | undefined,
  kek: Uint8Array,
  meta: { accountId: string; projectId: number | string },
): Uint8Array | null {
  try {
    if (!wrap) return null;
    if (wrap.wrap_version !== VAULT_WRAP_VERSION) return null;
    if (wrap.kdf !== VAULT_WRAP_KDF) return null;

    const iv = hexToBytes(wrap.wrap_iv_hex);
    const ct = hexToBytes(wrap.wrapped_vek_hex);
    const tag = hexToBytes(wrap.wrap_tag_hex);
    const sealed = new Uint8Array(ct.length + tag.length);
    sealed.set(ct);
    sealed.set(tag, ct.length);

    const aad = wrapAAD(
      meta.accountId,
      meta.projectId,
      wrap.principal_type,
      wrap.principal_id,
      wrap.vek_epoch,
    );
    const plain = gcm(kek, iv, aad).decrypt(sealed);
    return plain.length === 32 ? plain : null;
  } catch {
    return null;
  }
}

/** Convenience: derive the KEK from a root key and unwrap in one step. */
export function openVaultKey(
  wrap: VaultKeyWrap | null | undefined,
  accountRootKey: Uint8Array,
  accountId: string,
  projectId: number | string,
): Uint8Array | null {
  if (!wrap?.wrap_salt_hex) return null;
  const kek = deriveVaultKek(accountRootKey, accountId, projectId, wrap.wrap_salt_hex);
  return unwrapVaultKey(wrap, kek, { accountId, projectId });
}
